import { useEffect, useState } from 'react'
import { contentAPI } from '../services/api'
import type { CoachResult, Content, HashtagSuggestions } from '../types'
import { apiErrorMessage } from '../utils/apiError'

interface DraftEditorProps {
  content: Content
  onSaved: (content: Content) => void
  onCancel: () => void
}

/** The text with its first line swapped for another opening. */
export const withOpening = (text: string, opening: string): string => {
  const newline = text.indexOf('\n')
  return newline === -1 ? opening : opening + text.slice(newline)
}

/** The hashtags in a post's text, without the "#", in order, each once. */
export const tagsIn = (text: string): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const [, tag] of Array.from(text.matchAll(/#(\w+)/g))) {
    if (!seen.has(tag.toLowerCase())) {
      seen.add(tag.toLowerCase())
      out.push(tag)
    }
  }
  return out
}

/** The text with a hashtag added: onto its last line when that line is only
 *  hashtags, otherwise on a line of its own after a blank line. */
export const withHashtag = (text: string, tag: string): string => {
  const body = text.replace(/\s+$/, '')
  const lastLine = body.slice(body.lastIndexOf('\n') + 1)
  if (/^(#\w+\s*)+$/.test(lastLine)) return `${body} #${tag}`
  return body ? `${body}\n\n#${tag}` : `#${tag}`
}

/**
 * The draft's hashtags once the text is saved: the ones it had, less any
 * that were in its text and have been taken out, plus any now in the text.
 * Tags kept outside the text (a clip's, say) stay. Undefined when nothing
 * changed, so the save leaves them alone.
 */
export const hashtagsAfterEdit = (content: Content, text: string): string[] | undefined => {
  const before = content.hashtags || []
  const key = (tag: string) => tag.replace(/^#/, '').toLowerCase()
  const wasInText = new Set(tagsIn(content.text || '').map(key))
  const nowInText = tagsIn(text)
  const now = new Set(nowInText.map(key))
  const kept = before.filter((tag) => !(wasInText.has(key(tag)) && !now.has(key(tag))))
  const keptKeys = new Set(kept.map(key))
  const after = [...kept, ...nowInText.filter((tag) => !keptKeys.has(key(tag))).map((tag) => `#${tag}`)]
  const same = after.length === before.length && after.every((tag, i) => tag === before[i])
  return same ? undefined : after
}

/** What each platform says about how many to use, where it says so. */
const HASHTAG_ADVICE: Record<string, string> = {
  twitter: 'X recommends no more than 2 hashtags a post.',
  youtube: 'YouTube shows the first 3 above the title.',
}

/**
 * Editing a draft in Library. While the creator types, the same check every
 * post goes through when it goes out says what it needs, with a one-tap fix
 * when there's a sure one ("before fees" after the figure), and the length X
 * counts. "Other openings" offers three first lines to pick from, and
 * "Suggest hashtags" tags to add with a tap (not for blog posts).
 */
const DraftEditor = ({ content, onSaved, onCancel }: DraftEditorProps) => {
  const [text, setText] = useState(content.text || '')
  const [coach, setCoach] = useState<{ text: string; result: CoachResult } | null>(null)
  const [hooks, setHooks] = useState<string[] | null>(null)
  const [tags, setTags] = useState<HashtagSuggestions | null>(null)
  const [busy, setBusy] = useState<'hooks' | 'tags' | 'save' | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Checked a moment after typing stops; optional, so a failure shows nothing.
  useEffect(() => {
    let stopped = false
    const timer = setTimeout(() => {
      Promise.resolve()
        .then(() => contentAPI.check({ text, content_type: content.content_type, recap_date: content.recap_date }))
        .then((result) => !stopped && setCoach({ text, result }))
        .catch(() => undefined)
    }, 500)
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [text, content.content_type, content.recap_date])

  // Problems and fixes are for the text as it was checked, never stale.
  const current = coach && coach.text === text ? coach.result : null

  const applyFix = (fix: { at: number; insert: string }) => setText(text.slice(0, fix.at) + fix.insert + text.slice(fix.at))

  const loadHooks = async () => {
    setBusy('hooks')
    setError(null)
    try {
      setHooks(await contentAPI.hooks(content.id))
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't write other openings."))
    } finally {
      setBusy(null)
    }
  }

  const loadTags = async () => {
    setBusy('tags')
    setError(null)
    try {
      setTags(await contentAPI.suggestHashtags(text, content.content_type))
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't suggest hashtags."))
    } finally {
      setBusy(null)
    }
  }

  const save = async () => {
    setBusy('save')
    setError(null)
    try {
      const hashtags = hashtagsAfterEdit(content, text)
      onSaved(await contentAPI.update(content.id, hashtags ? { text, hashtags } : { text }))
    } catch (e) {
      setError(apiErrorMessage(e, "Couldn't save it."))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="mb-4 space-y-2" data-testid="draft-editor">
      <textarea
        aria-label="Post text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={Math.min(14, Math.max(5, text.split('\n').length + 2))}
        className="w-full rounded-md border border-gray-300 p-2 text-sm"
      />
      {current && (
        <div className="space-y-1 text-xs" data-testid="coach">
          {current.problems.map((p, i) => (
            <p key={i} className="flex flex-wrap items-center gap-2 text-amber-800" role="note">
              <span>{p.message}</span>
              {p.fix && (
                <button
                  type="button"
                  onClick={() => applyFix(p.fix!)}
                  className="rounded border border-amber-300 px-2 py-0.5 font-medium hover:bg-amber-50"
                >
                  Add &ldquo;{p.fix.insert.trim()}&rdquo;
                </button>
              )}
            </p>
          ))}
          {current.problems.length === 0 && <p className="text-success-700">Ready to post.</p>}
          {current.notes.map((note) => (
            <p key={note} className="text-gray-500">
              {note}
            </p>
          ))}
          {current.length && (
            <p className={current.length.count > current.length.limit ? 'text-danger-700' : 'text-gray-500'}>
              {current.length.count} / {current.length.limit} characters with the disclaimer
            </p>
          )}
        </div>
      )}

      {hooks && hooks.length > 0 && (
        <div className="rounded-md border border-gray-200 p-2" aria-label="Other openings" role="group">
          <p className="text-xs font-medium text-gray-700">Pick an opening line</p>
          <ul className="mt-1 space-y-1">
            {hooks.map((hook) => (
              <li key={hook}>
                <button
                  type="button"
                  onClick={() => {
                    setText(withOpening(text, hook))
                    setHooks(null)
                  }}
                  className="w-full rounded px-2 py-1 text-left text-sm text-gray-800 hover:bg-gray-50"
                >
                  {hook}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tags && (
        <div className="rounded-md border border-gray-200 p-2" aria-label="Suggested hashtags" role="group">
          <p className="text-xs font-medium text-gray-700">Tap a hashtag to add it</p>
          {(() => {
            const inText = new Set(tagsIn(text).map((tag) => tag.toLowerCase()))
            const left = tags.hashtags.filter((h) => !inText.has(h.hashtag.toLowerCase()))
            return left.length > 0 ? (
              <div className="mt-1 flex flex-wrap gap-1">
                {left.map((h) => (
                  <button
                    key={h.hashtag}
                    type="button"
                    title={h.reason || undefined}
                    onClick={() => setText(withHashtag(text, h.hashtag))}
                    className="rounded bg-primary-50 px-2 py-0.5 text-sm text-primary-700 hover:bg-primary-100"
                  >
                    #{h.hashtag}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                {tags.hashtags.length ? 'All added.' : 'No hashtags to suggest for this post.'}
              </p>
            )
          })()}
          <p className="mt-1 text-xs text-gray-500">
            {tags.source === 'keywords' ? "From the post's own words (no AI). " : ''}
            {HASHTAG_ADVICE[content.content_type] || ''}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={busy !== null || !text.trim() || text === content.text}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy === 'save' ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={loadHooks}
          disabled={busy !== null || !text.trim()}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy === 'hooks' ? 'Writing…' : 'Other openings'}
        </button>
        {content.content_type !== 'blog' && (
          <button
            type="button"
            onClick={loadTags}
            disabled={busy !== null || !text.trim()}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {busy === 'tags' ? 'Finding…' : 'Suggest hashtags'}
          </button>
        )}
        <button type="button" onClick={onCancel} className="px-2 py-1.5 text-sm text-gray-600">
          Cancel
        </button>
      </div>
      {error && (
        <p role="alert" className="text-sm text-danger-700">
          {error}
        </p>
      )}
    </div>
  )
}

export default DraftEditor
